import { useCallback, useEffect, useMemo, useState } from 'react';

export type BulkSelection = {
	selectedIds: string[];
	count: number;
	/** Every listed id is selected (and there is at least one). */
	allSelected: boolean;
	/** Some but not all listed ids are selected. */
	someSelected: boolean;
	isSelected: (id: string) => boolean;
	toggle: (id: string) => void;
	toggleAll: () => void;
	clear: () => void;
};

/**
 * Checkbox selection over a list of ids. Ids that leave the list (deleted,
 * filtered out, next page) are dropped from the selection automatically, so a
 * "delete selected" action never targets rows the user can no longer see.
 * Pass a memoised `ids` array.
 */
export function useBulkSelection(ids: string[]): BulkSelection {
	const [selected, setSelected] = useState<ReadonlySet<string>>(() => new Set());

	useEffect(() => {
		setSelected((prev) => {
			if (prev.size === 0) return prev;
			const listed = new Set(ids);
			const next = new Set([...prev].filter((id) => listed.has(id)));
			return next.size === prev.size ? prev : next;
		});
	}, [ids]);

	const toggle = useCallback((id: string) => {
		setSelected((prev) => {
			const next = new Set(prev);
			if (next.has(id)) next.delete(id);
			else next.add(id);
			return next;
		});
	}, []);

	const toggleAll = useCallback(() => {
		setSelected((prev) => (prev.size === ids.length && ids.length > 0 ? new Set() : new Set(ids)));
	}, [ids]);

	const clear = useCallback(() => setSelected(new Set()), []);

	return useMemo(() => {
		const count = selected.size;
		return {
			selectedIds: [...selected],
			count,
			allSelected: ids.length > 0 && count === ids.length,
			someSelected: count > 0 && count < ids.length,
			isSelected: (id: string) => selected.has(id),
			toggle,
			toggleAll,
			clear,
		};
	}, [selected, ids, toggle, toggleAll, clear]);
}
